import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Asset, Site, SiteArea, Task } from 'graphql-common';
import Form from '@lib/components/ReactHookForm/FormContainer';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';
import { UseTaskAndCompletionsPickerResult } from '@lib/components/TaskCompletions/TaskAndCompletionsModal/useTaskAndCompletionsPicker';
import useInterventionLinkedCompletions from '@lib/components/TaskCompletions/utils/useInterventionLinkedCompletions';
import getInterventionFormFields from '@lib/utils/intervention/getInterventionFormFields';
import useSearchListQueries from '../utils/useSearchListQueries';
import { UseModalManagementResult } from '../utils/useModalManagement';
import getInterventionDefaultValuesFromQueryData from '../utils/getInterventionDefaultValuesFromQueryData';
import { UseInterventionActionsResult } from '../utils/useInterventionActions';
import getInterventionDefaultValues from '../utils/getInterventionDefaultValues';
import { InterventionViewMode } from '../enums';
import { InterventionCreatePrefillState } from '../types';
import styles from './InterventionFormPage.module.scss';

type Props = {
  useModalManagementResult: UseModalManagementResult;
  useInterventionActionsResult: UseInterventionActionsResult;
  viewMode: InterventionViewMode;
  formRef: FormRefType;
  isCategoriesViewOnly: boolean;
  taskAndCompletionsPicker: UseTaskAndCompletionsPickerResult;
  isTasksAvailable: boolean;
};

export default function InterventionFormPage({
  useModalManagementResult,
  useInterventionActionsResult,
  viewMode,
  formRef,
  isCategoriesViewOnly,
  taskAndCompletionsPicker,
  isTasksAvailable,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  // Set by a bulk "Create intervention" action started from a selection of
  // completions elsewhere (Task Reporting) — see getInterventionDefaultValues.
  const taskCompletionsPrefill = (
    state as InterventionCreatePrefillState | null
  )?.taskCompletions;
  const useSearchListQueriesResult = useSearchListQueries();
  const {
    id,
    mutationError,
    interventionQueryHookResult,
    loading,
    onSubmit,
    onViewChange,
    usersQueryHookResult,
    interventionSitePrefillDataQueryHookResult,
    interventionSiteAreaPrefillDataQueryHookResult,
    interventionAssetPrefillDataQueryHookResult,
    interventionTaskPrefillDataQueryHookResult,
  } = useInterventionActionsResult;
  const { allIds: assigneeIds = [] } = usersQueryHookResult;

  const isCreatePage = viewMode === InterventionViewMode.Add;

  const linkedCompletions = useInterventionLinkedCompletions({
    interventionId: id,
    taskGroupKey: interventionQueryHookResult.data?.task?.groupKey,
    skip: isCreatePage || !isTasksAvailable,
  });

  const defaultValues = isCreatePage
    ? getInterventionDefaultValues({
        site: interventionSitePrefillDataQueryHookResult?.data?.site as Site,
        siteArea: interventionSiteAreaPrefillDataQueryHookResult?.data
          ?.siteArea as SiteArea,
        asset: interventionAssetPrefillDataQueryHookResult?.data
          ?.asset as Asset,
        // A hidden Task field must not carry a prefilled link: without
        // TASKS_MANAGEMENT write the create would be FORBIDDEN.
        task: isTasksAvailable
          ? (interventionTaskPrefillDataQueryHookResult?.data?.task as Task)
          : undefined,
        completions: taskCompletionsPrefill,
      })
    : getInterventionDefaultValuesFromQueryData(
        interventionQueryHookResult.data,
        assigneeIds,
        linkedCompletions.options,
      );

  const fields = getInterventionFormFields({
    assigneeIds,
    interventionId: id,
    t,
    isEditForm: viewMode === InterventionViewMode.Edit,
    isTasksAvailable,
    getAssetsLoadOptions: useSearchListQueriesResult.getAssetsLoadOptions,
    getDepartmentsLoadOptions:
      useSearchListQueriesResult.getDepartmentsLoadOptions,
    getInterventionCategoriesLoadOptions:
      useSearchListQueriesResult.getInterventionCategoriesLoadOptions,
    getSiteAreasLoadOptions: useSearchListQueriesResult.getSiteAreasLoadOptions,
    getSitesLoadOptions: useSearchListQueriesResult.getSitesLoadOptions,
    getSupervisorLoadOptions:
      useSearchListQueriesResult.getSupervisorLoadOptions,
    getAssigneeLoadOptions: useSearchListQueriesResult.getAssigneeLoadOptions,
    getTasksLoadOptions: useSearchListQueriesResult.getTasksLoadOptions,
    openAssetsModalTableMultiselect:
      useModalManagementResult.openAssetsModalTableMultiselect,
    openCategoryFormModal: useModalManagementResult.openCategoryFormModal,
    openCompletionsStep: taskAndCompletionsPicker.openCompletionsStep,
    openDepartmentsModalTableMultiselect:
      useModalManagementResult.openDepartmentsModalTableMultiselect,
    openTaskStep: taskAndCompletionsPicker.openTaskStep,
    isCategoriesViewOnly,
    onSiteChange: taskAndCompletionsPicker.onSiteChange,
    onSiteAreaChange: taskAndCompletionsPicker.onSiteAreaChange,
    onAssetChange: taskAndCompletionsPicker.onAssetChange,
  });

  const isFormLoading = loading || linkedCompletions.loading;

  const actionsComponent = () => (
    <ActionsRow
      className={formStyles.formActions}
      actions={[
        <Button
          onClick={() => navigate(-1)}
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          fullWidth
        />,
        <Button
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t(isCreatePage ? 'create' : 'save-changes')}
          leftIcon="check"
          type="submit"
          disabled={isFormLoading}
          isLoading={isFormLoading}
          fullWidth
        />,
      ]}
    />
  );

  return (
    <DashboardContentMaxWidth>
      <PageHeader
        title={t(isCreatePage ? 'create-intervention' : 'edit-intervention')}
      >
        {isCreatePage ? undefined : (
          <Button
            buttonText={t('view')}
            buttonType={ButtonTypes.primaryOutlined}
            leftIcon="visibility"
            onClick={() => onViewChange(InterventionViewMode.View)}
          />
        )}
      </PageHeader>
      <PageBody>
        <WhiteBox verticalPaddings="large">
          <Form
            actionsComponent={actionsComponent}
            actionsComponentProps={{ isCreatePage }}
            defaultValues={defaultValues}
            fields={fields}
            formId="intervention"
            formRef={formRef}
            graphQLErrors={getGraphQLErrors(mutationError)}
            graphQLErrorsReplacePathMap={{
              taskCompletions: FieldNames.Completions,
            }}
            loading={isFormLoading}
            onSubmit={onSubmit}
          />
        </WhiteBox>
        {isCreatePage ? (
          <div />
        ) : (
          <div className={styles.actions}>
            <Button
              buttonText={t('delete-intervention')}
              buttonType={ButtonTypes.negativeOutlined}
              leftIcon="delete"
              leftIconClassName={materialSymbolsOutlined}
              onClick={useModalManagementResult.openInterventionDestroyModal}
            />
          </div>
        )}
      </PageBody>
    </DashboardContentMaxWidth>
  );
}
